// =====================================================
// SUBSPECIALTIES ADMIN PAGE - Con import/export Excel
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Layers,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
} from 'lucide-react';
import { subspecialtiesApi } from '../../api/subspecialties';
import { specialtiesApi } from '../../api/specialties';
import { subspecialtiesImportExport } from '../../api/import-export';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal } from '../../components/ui/ImportExportModal';
import { toast } from '../../components/ui/Toast';
import type { Subspecialty, SubspecialtyFormData, CreateSubspecialtyRequest, UpdateSubspecialtyRequest, SpecialtySelect } from '../../types/catalog-admin';
import type { PaginatedResponse } from '../../types/pagination';

type SortField = 'name' | 'code' | 'sortOrder' | 'createdAt';

export function SubspecialtiesAdminPage() {
  const [data, setData] = useState<PaginatedResponse<Subspecialty> | null>(null);
  const [specialties, setSpecialties] = useState<SpecialtySelect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal crear/editar
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Subspecialty | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Modal import/export
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  // Confirmación
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: Subspecialty | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<SubspecialtyFormData>({
    defaultValues: { specialtyId: '', code: '', name: '', description: '', sortOrder: 0 },
  });

  const loadSpecialties = useCallback(async () => {
    try {
      const result = await specialtiesApi.getForSelect();
      setSpecialties(result);
    } catch (err) {
      console.error('Error loading specialties:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await subspecialtiesApi.getAllPaginated({
        page, limit, search: searchQuery || undefined, sortBy, sortOrder, includeInactive: showInactive,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar subespecialidades');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive]);

  useEffect(() => { loadSpecialties(); }, [loadSpecialties]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setPage(1); };

  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({ specialtyId: specialties[0]?.id || '', code: '', name: '', description: '', sortOrder: 0 });
    setIsModalOpen(true);
  };

  const handleEdit = (item: Subspecialty) => {
    setEditingItem(item);
    setFormError(null);
    reset({ specialtyId: item.specialtyId, code: item.code, name: item.name, description: item.description || '', sortOrder: item.sortOrder });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: SubspecialtyFormData) => {
    // Limpiar error previo
    setFormError(null);

    // Validar código duplicado (global)
    const codeUpper = formData.code.toUpperCase().trim();
    const existingItem = data?.data.find(
      (item) => item.code.toUpperCase() === codeUpper && item.id !== editingItem?.id
    );

    if (existingItem) {
      setFormError(`Ya existe una subespecialidad con el código "${codeUpper}"`);
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        const updateData: UpdateSubspecialtyRequest = {
          specialtyId: formData.specialtyId, code: formData.code, name: formData.name,
          description: formData.description || undefined, sortOrder: formData.sortOrder,
        };
        await subspecialtiesApi.update(editingItem.id, updateData);
        toast.success('Actualizado', `Subespecialidad "${formData.name}" actualizada`);
      } else {
        const createData: CreateSubspecialtyRequest = {
          specialtyId: formData.specialtyId, code: formData.code, name: formData.name,
          description: formData.description || undefined, sortOrder: formData.sortOrder,
        };
        await subspecialtiesApi.create(createData);
        toast.success('Creado', `Subespecialidad "${formData.name}" creada`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
      loadData();
    } catch (err: any) {
      const codeUpper = formData.code.toUpperCase().trim();
      setFormError(`Ya existe una subespecialidad con el código "${codeUpper}"`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: Subspecialty) => {
    setConfirmDialog({ isOpen: true, item, action: item.isActive ? 'deactivate' : 'activate' });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await subspecialtiesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `Subespecialidad "${confirmDialog.item.name}" desactivada`);
      } else {
        await subspecialtiesApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `Subespecialidad "${confirmDialog.item.name}" activada`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  const items = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subespecialidades</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión del catálogo de subespecialidades</p>
        </div>
      </div>

      {/* Toolbar - Nuevo diseño */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        {/* Fila superior: Búsqueda y acciones principales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Búsqueda y botón filtros */}
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código, nombre o especialidad..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters 
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Importar / Exportar
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nueva subespecialidad
            </button>
          </div>
        </div>

        {/* Fila de filtros activos (chips) */}
        {(showInactive || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Estado: Incluye inactivos
                <button 
                  onClick={() => { setShowInactive(false); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button 
                  onClick={() => { setSearchQuery(''); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setShowInactive(false);
                setSearchQuery('');
                setPage(1);
              }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Fila inferior: Ordenación y paginación */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 pt-3 gap-4">
            {/* Checkbox y ordenación */}
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Mostrar inactivos
                </span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                    setPage(1);
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Fecha (Más reciente)</option>
                  <option value="createdAt-asc">Fecha (Antiguo)</option>
                </select>
              </div>
            </div>

            {/* Items por página */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Ver:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => { setLimit(10); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 10 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  10
                </button>
                <button
                  onClick={() => { setLimit(25); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 25 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  25
                </button>
                <button
                  onClick={() => { setLimit(50); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    limit === 50 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  50
                </button>
              </div>
              <span className="text-sm text-gray-500">por página</span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {items.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Layers className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">{searchQuery ? 'Sin resultados' : 'No hay subespecialidades'}</h3>
            <p className="text-gray-500">{searchQuery ? 'No se encontraron subespecialidades con ese criterio' : 'Crea tu primera subespecialidad'}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Especialidad</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Grupos</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-cyan-50 text-cyan-700 text-xs font-semibold rounded-lg">{item.code}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>{item.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.specialtyName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">{item.treatmentGroupsCount ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button onClick={() => handleToggleStatus(item)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${item.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${item.isActive ? 'translate-x-5' : 'translate-x-0'}`}>
                            <Power className={`h-3 w-3 absolute top-1 left-1 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"><Edit2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">Mostrando {(meta.page - 1) * meta.limit + 1} a {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(meta.page - 1)} disabled={!meta.hasPrevPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm text-gray-700">Página {meta.page} de {meta.totalPages}</span>
                  <button onClick={() => setPage(meta.page + 1)} disabled={!meta.hasNextPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingItem ? 'Editar subespecialidad' : 'Nueva subespecialidad'} subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos'}>
        <form onSubmit={handleSubmit(handleSave)}>
          {/* Error banner dentro del modal */}
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Error de validación</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Especialidad *</label>
              <select
                {...register('specialtyId', { required: 'Selecciona una especialidad' })}
                onChange={() => formError && setFormError(null)}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.specialtyId ? 'border-red-300' : 'border-gray-200'}`}
              >
                <option value="">Seleccionar...</option>
                {specialties.map((s) => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
              </select>
              {errors.specialtyId && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.specialtyId.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
              <input
                type="text"
                maxLength={20}
                {...register('code', { 
                  required: 'El código es obligatorio',
                  maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                })}
                onChange={() => formError && setFormError(null)}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${errors.code || formError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`}
                placeholder="SUB001"
              />
              {errors.code && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.code.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                type="text"
                maxLength={100}
                {...register('name', { 
                  required: 'El nombre es obligatorio',
                  maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                })}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.name ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="Cirugía Oral"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.name.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                maxLength={500}
                {...register('description', {
                  maxLength: { value: 500, message: 'Máximo 500 caracteres' }
                })}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none"
                placeholder="Descripción opcional..."
              />
            </div>
          </div>
          <ModalFooter>
            <button type="button" onClick={handleCloseModal} disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"><X className="h-4 w-4" />Cancelar</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />{editingItem ? 'Guardar' : 'Crear'}</>}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        title="Subespecialidades"
        onExport={subspecialtiesImportExport.export}
        onDownloadTemplate={subspecialtiesImportExport.downloadTemplate}
        onPreview={subspecialtiesImportExport.preview}
        onImport={subspecialtiesImportExport.import}
        onImportSuccess={loadData}
        previewColumns={[
          { key: 'specialtyCode', label: 'Cód. Especialidad' },
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
        ]}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar subespecialidad?' : '¿Activar subespecialidad?'}
        message={confirmDialog.action === 'deactivate' ? `La subespecialidad "${confirmDialog.item?.name}" será desactivada.` : `La subespecialidad "${confirmDialog.item?.name}" será reactivada.`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default SubspecialtiesAdminPage;
